import { create } from 'zustand'

const useAppStore = create((set, get) => ({
  // Portal state
  isPortalOpen: true,
  portalTransitioning: false,

  // Map state
  selectedCounty: null,
  drilldownUAT: false,
  mapZoom: 1,
  mapCenter: [25.0, 46.0],
  mapOffset: { x: 0, y: 0 },
  
  // GEOJSON Data
  countiesData: null,
  uatData: null,
  loading: false,
  error: null,

  // Data state
  routesData: null,
  invoicesData: null,
  activationsData: null,
  filteredData: null,
  dataPreview: [],
  mappingData: [],
  
  // Filters
  selectedDivision: 'all',
  selectedColumns: [],
  availableColumns: [],

  // Upload state
  uploadProgress: 0,
  isUploading: false,
  uploadError: null,

  // Actions
  setPortalOpen: (isOpen) => set({ isPortalOpen: isOpen }),
  setPortalTransitioning: (transitioning) => set({ portalTransitioning: transitioning }),
  
  setSelectedCounty: (county) => set({ selectedCounty: county }),
  setDrilldownUAT: (drilldown) => set({ drilldownUAT: drilldown }),
  setMapZoom: (zoom) => set({ mapZoom: zoom }),
  setMapCenter: (center) => set({ mapCenter: center }),
  setMapOffset: (offset) => set({ mapOffset: offset }),
  
  setCountiesData: (data) => set({ countiesData: data }),
  setUatData: (data) => set({ uatData: data }),
  setLoading: (loading) => set({ loading: loading }),
  setError: (error) => set({ error: error }),
  
  setRoutesData: (data) => set({ routesData: data }),
  setInvoicesData: (data) => set({ invoicesData: data }),
  setActivationsData: (data) => set({ activationsData: data }),
  setFilteredData: (data) => set({ filteredData: data }),
  setDataPreview: (preview) => set({ dataPreview: preview }),
  setMappingData: (mapping) => set({ mappingData: mapping }),
  
  setSelectedDivision: (division) => set({ selectedDivision: division }),
  setSelectedColumns: (columns) => set({ selectedColumns: columns }),
  setAvailableColumns: (columns) => set({ availableColumns: columns }),

  // Upload actions
  setUploadProgress: (progress) => set({ uploadProgress: progress }),
  setIsUploading: (uploading) => set({ isUploading: uploading }),
  setUploadError: (error) => set({ uploadError: error }),

  // Actions pentru harta
  loadCountiesData: async () => {
    set({ loading: true, error: null })
    try {
      const response = await fetch('https://pub-48b2e4d8b097455e9c21be056e956ce3.r2.dev/RO.geojson')
      if (!response.ok) throw new Error('Failed to load counties data')
      const data = await response.json()
      set({ countiesData: data, loading: false })
    } catch (error) {
      set({ error: error.message, loading: false })
    }
  },

  loadUatData: async () => {
    set({ loading: true, error: null })
    try {
      const response = await fetch('https://pub-48b2e4d8b097455e9c21be056e956ce3.r2.dev/ro_uat.geojson')
      if (!response.ok) throw new Error('Failed to load UAT data')
      const data = await response.json()
      set({ uatData: data, loading: false })
    } catch (error) {
      set({ error: error.message, loading: false })
    }
  },

  // Data processing functions
  processXLSXData: async (file, dataType) => {
    set({ isUploading: true, uploadProgress: 0, uploadError: null })
    
    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        set(state => {
          if (state.uploadProgress >= 90) {
            clearInterval(progressInterval)
            return { uploadProgress: 90 }
          }
          return { uploadProgress: state.uploadProgress + 10 }
        })
      }, 200)

      // Read and process file
      const data = await readAndProcessXLSX(file)
      clearInterval(progressInterval)
      
      // Normalize data
      const normalizedData = normalizeData(data, dataType)
      
      // Update store based on data type
      switch (dataType) {
        case 'routes':
          set({ 
            routesData: normalizedData,
            filteredData: normalizedData,
            dataPreview: normalizedData.slice(0, 10),
            availableColumns: Object.keys(normalizedData[0] || {}),
            selectedColumns: Object.keys(normalizedData[0] || {}),
            uploadProgress: 100,
            isUploading: false 
          })
          break
        case 'invoices':
          set({ 
            invoicesData: normalizedData,
            uploadProgress: 100,
            isUploading: false 
          })
          break
        case 'activations':
          set({ 
            activationsData: normalizedData,
            uploadProgress: 100,
            isUploading: false 
          })
          break
      }

      // Auto-map to geography
      setTimeout(() => {
        get().autoMapToGeography(dataType)
      }, 500)

    } catch (error) {
      set({ 
        uploadError: error.message,
        isUploading: false,
        uploadProgress: 0 
      })
    }
  },

  autoMapToGeography: (dataType) => {
    const { routesData, invoicesData, activationsData, mappingData } = get()
    let dataToMap
    
    switch (dataType) {
      case 'routes':
        dataToMap = routesData
        break
      case 'invoices':
        dataToMap = invoicesData
        break
      case 'activations':
        dataToMap = activationsData
        break
      default:
        dataToMap = routesData || invoicesData || activationsData
    }

    if (!dataToMap || dataToMap.length === 0) return

    // Simple geographic mapping simulation
    const mapped = dataToMap.map(item => ({
      ...item,
      mapped: true,
      coordinates: [
        25 + Math.random() * 5 - 2.5, // Random coordinates in Romania
        45.5 + Math.random() * 2 - 1
      ]
    }))

    set({ mappingData: mapped })
  },

  filterDataByDivision: (division) => {
    const { routesData } = get()
    if (!routesData) return

    if (division === 'all') {
      set({ filteredData: routesData })
    } else {
      const filtered = routesData.filter(item => 
        item.divizia && item.divizia.toUpperCase().includes(division.toUpperCase())
      )
      set({ filteredData: filtered, dataPreview: filtered.slice(0, 10) })
    }
  },

  sendToMap: () => {
    const { mappingData, filteredData } = get()
    const dataToSend = mappingData.length > 0 ? mappingData : filteredData
    
    if (dataToSend && dataToSend.length > 0) {
      console.log('Sending to map:', dataToSend.length, 'records')
      // Here we would trigger map visualization
    }
  },

  // Derived selectors
  getActiveData: () => {
    const { routesData, invoicesData, activationsData } = get()
    return { routesData, invoicesData, activationsData }
  },

  getCountyData: () => {
    const { selectedCounty, filteredData } = get()
    if (!selectedCounty || !filteredData) return null
    
    return filteredData.filter(item => item.judet === selectedCounty)
  },

  getSelectedCountyUATs: () => {
    const { selectedCounty, uatData } = get()
    if (!selectedCounty || !uatData) return []
    
    return uatData.features.filter(feature => 
      feature.properties?.judet?.toUpperCase() === selectedCounty.toUpperCase()
    )
  }
}))

// Helper functions for XLSX processing
const readAndProcessXLSX = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    
    reader.onload = (e) => {
      try {
        const data = e.target.result
        // XLSX will be loaded dynamically
        if (typeof XLSX === 'undefined') {
          import('xlsx').then(module => {
            const XLSX = module
            const workbook = XLSX.read(data, { type: 'binary' })
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
            const jsonData = XLSX.utils.sheet_to_json(firstSheet)
            resolve(jsonData)
          }).catch(reject)
        } else {
          const workbook = XLSX.read(data, { type: 'binary' })
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
          const jsonData = XLSX.utils.sheet_to_json(firstSheet)
          resolve(jsonData)
        }
      } catch (error) {
        reject(new Error('Failed to parse XLSX file: ' + error.message))
      }
    }
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'))
    }
    
    reader.readAsBinaryString(file)
  })
}

const normalizeData = (data, dataType) => {
  if (!data || data.length === 0) return []

  return data.map(item => {
    const normalized = {}
    
    Object.keys(item).forEach(key => {
      let value = item[key]
      
      // Convert to string for normalization
      if (value !== null && value !== undefined) {
        value = value.toString()
        
        // Remove diacritics
        value = value.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        
        // Remove extra spaces
        value = value.replace(/\s+/g, ' ').trim()
        
        // Uppercase for consistency
        value = value.toUpperCase()
      }
      
      // Create normalized key (remove diacritics and spaces)
      const normalizedKey = key.normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, '_')
        .toUpperCase()
      
      normalized[normalizedKey] = value
    })
    
    // Add data type identifier
    normalized.DATA_TYPE = dataType.toUpperCase()
    
    return normalized
  })
}

export { useAppStore }
